import { CurrencyType as _CurrencyType } from '../accounts/account';

export enum CurrencyType {
  ARS = _CurrencyType.ARS,
  USD = _CurrencyType.USD
}

export enum OfferingType {
  RECURRING = 'recurring',
  ONE_TIME = 'one_time'
}

export enum ServiceType {
  CREMACIONES = 'cremaciones',
  ABONOS = 'abonos',
  PARCELAS = 'parcelas'
}

export enum OfferingStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ARCHIVED = 'archived'
}

export enum BillingFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  BIMONTHLY = 'bimonthly',
  QUARTERLY = 'quarterly',
  SEMIANNUAL = 'semiannual',
  ANNUAL = 'annual',
  ONE_TIME = 'one_time'
}

export interface OfferingFilters {
  search?: string;
  type?: OfferingType | undefined;
  serviceType?: ServiceType | undefined;
  status?: OfferingStatus | undefined;
  limit?: number;
  offset?: number;
  isAdditional?: boolean;
  includePrices?: boolean;
}

export type OfferingPrice = {
  id: string;
  offeringId: string;
  price: number;
  currency: CurrencyType;
  effectiveFrom: Date;
  effectiveUntil?: Date;
}

export type OfferingAgePricingRange = {
  rangeStart: number;
  rangeEnd: number;
  price: number;
}

export type OfferingAgePricing = {
  id: string;
  offeringId: string;
  offeringPriceHistoryId: string | null;
  ranges: OfferingAgePricingRange[];
  status: 'active' | 'inactive';
  createdAt: Date;
  updatedAt: Date;
}

export type CreateOfferingAgePricingDto = {
  offeringPriceHistoryId?: string;
  ranges: OfferingAgePricingRange[];
  status?: 'active' | 'inactive';
}

export type UpdateOfferingAgePricingDto = Partial<CreateOfferingAgePricingDto>;

export type OfferingPriceDto = Omit<OfferingPrice, 'id' | 'offeringId'> & {
  agePricingRanges?: OfferingAgePricingRange[];
};

export interface Offering {
  id: string;
  name: string;
  code: string;
  serviceType: ServiceType;
  status: OfferingStatus;
  description?: string;
  offeringType: OfferingType;
  basePrice: number;
  currency: CurrencyType;
  billingFrequency: BillingFrequency;
  taxRate: number;
  isTaxable: boolean;
  isInvoiceable: boolean;
  hasMultipleOffered: boolean;
  hasPaymentPlanAvailable: boolean;
  hasAgePricing: boolean;
  isAdditional: boolean;
  prices?: OfferingPrice[];
  createdAt: Date;
  updatedAt: Date;
}

export interface OfferingDto {
  name: string;
  code: string;
  serviceType: ServiceType | null;
  description?: string;
  offeringType: OfferingType;
  basePrice: number;
  currency: CurrencyType;
  billingFrequency: BillingFrequency;
  taxRate: number;
  isTaxable: boolean;
  isInvoiceable: boolean;
  hasMultipleOffered: boolean;
  hasPaymentPlanAvailable: boolean;
  hasAgePricing: boolean;
  isAdditional?: boolean;
}

export const formatOfferingStatus = (status: OfferingStatus): string => {
  const statuses: Record<OfferingStatus, string> = {
    [OfferingStatus.ACTIVE]: 'Activo',
    [OfferingStatus.INACTIVE]: 'Inactivo',
    [OfferingStatus.ARCHIVED]: 'Archivado',
  }
  return statuses[status] || status
}

export const formatBillingFrequency = (cycle: BillingFrequency): string => {
  const cycles: Record<BillingFrequency, string> = {
    [BillingFrequency.DAILY]: 'Diario',
    [BillingFrequency.WEEKLY]: 'Semanal',
    [BillingFrequency.MONTHLY]: 'Mensual',
    [BillingFrequency.BIMONTHLY]: 'Bimestral',
    [BillingFrequency.QUARTERLY]: 'Trimestral',
    [BillingFrequency.SEMIANNUAL]: 'Semestral',
    [BillingFrequency.ANNUAL]: 'Anual',
    [BillingFrequency.ONE_TIME]: 'Única',
  }
  return cycles[cycle] || cycle
}

export const formatBillingFrequencyOnLabel = (cycle?: BillingFrequency): string => {
  const cycles: Record<BillingFrequency, string> = {
    [BillingFrequency.DAILY]: 'diario',
    [BillingFrequency.WEEKLY]: 'semanal',
    [BillingFrequency.MONTHLY]: 'mensual',
    [BillingFrequency.BIMONTHLY]: 'bimestral',
    [BillingFrequency.QUARTERLY]: 'trimestral',
    [BillingFrequency.SEMIANNUAL]: 'semestral',
    [BillingFrequency.ANNUAL]: 'anual',
    [BillingFrequency.ONE_TIME]: '',
  }
  return cycle ? cycles[cycle] : ''
}

export const formatBillingFrequencyShort = (cycle: BillingFrequency): string => {
  const cycles: Record<BillingFrequency, string> = {
    [BillingFrequency.DAILY]: '/dia',
    [BillingFrequency.WEEKLY]: '/semana',
    [BillingFrequency.MONTHLY]: '/mes',
    [BillingFrequency.BIMONTHLY]: '/2meses',
    [BillingFrequency.QUARTERLY]: '/3meses',
    [BillingFrequency.SEMIANNUAL]: '/6meses',
    [BillingFrequency.ANNUAL]: '/año',
    [BillingFrequency.ONE_TIME]: ' ',
  }
  return cycles[cycle] || cycle
}

export const getOfferingStatusColor = (status: OfferingStatus): string => {
  switch (status) {
    case OfferingStatus.ACTIVE:
      return 'text-green-600'
    case OfferingStatus.INACTIVE:
      return 'text-warning-600'
    case OfferingStatus.ARCHIVED:
      return 'text-red-600'
    default:
      return 'text-gray-600'
  }
}